// src/pages/school/Exams/ExamsPage.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Card, Row, Col, Button, Badge, Form, InputGroup, Spinner, Alert,
  Dropdown, ButtonGroup, Toast, ToastContainer, Modal, OverlayTrigger, Tooltip, ListGroup
} from "react-bootstrap";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import {
  collection, onSnapshot, query, orderBy, limit, where,
  deleteDoc, doc, getDocs, writeBatch, addDoc, setDoc, serverTimestamp, updateDoc
} from "firebase/firestore";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";

dayjs.extend(relativeTime);

/* ---------- Reusable: Checkbox multi-select dropdown with search ---------- */
const MultiSelectDropdown = ({
  options = [],
  value = [],
  onChange,
  disabled = false,
  placeholder = "Select…",
  size = "md",
  menuMaxHeight = 340,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())),
    [options, q]
  );

  const allSelected = value.length === options.length && options.length > 0;
  const someSelected = value.length > 0 && !allSelected;

  const toggleOne = (val) => {
    const next = value.includes(val) ? value.filter((v) => v !== val) : [...value, val];
    onChange(next);
  };

  const selectAll = () => onChange(options.map((o) => o.value));
  const clearAll = () => onChange([]);

  const labelText = value.length ? `${value.length} selected` : placeholder;

  return (
    <Dropdown show={open} onToggle={setOpen} className="w-100" autoClose="outside" renderMenuOnMount>
      <Dropdown.Toggle variant="outline-secondary" className="w-100 text-start" disabled={disabled} size={size}>
        {labelText}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="w-100 p-2 shadow-sm"
        style={{ maxHeight: menuMaxHeight, overflowY: "auto", zIndex: 2000, position: "absolute" }}
      >
        <Form.Control
          size="sm"
          placeholder="Search…"
          className="mb-2"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="d-flex justify-content-between align-items-center px-1 pb-2 border-bottom mb-2">
          <Form.Check
            type="checkbox"
            id="ms-select-all"
            label="Select all"
            checked={allSelected}
            ref={(el) => el && (el.indeterminate = someSelected)}
            onChange={() => (allSelected ? clearAll() : selectAll())}
          />
          <Button size="sm" variant="link" onClick={clearAll}>
            Clear
          </Button>
        </div>
        {filtered.map((opt) => (
          <Form.Check
            key={opt.value}
            type="checkbox"
            className="px-1 py-1"
            label={opt.label}
            checked={value.includes(opt.value)}
            onChange={() => toggleOne(opt.value)}
          />
        ))}
        {!filtered.length && <div className="text-muted small px-2 py-1">No matches</div>}
      </Dropdown.Menu>
    </Dropdown>
  );
};

/* ---------- Utils ---------- */
const toJsDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") { try { return v.toDate(); } catch { return null; } }
  if (v instanceof Date) return isNaN(v) ? null : v;
  const d = new Date(String(v));
  return isNaN(d) ? null : d;
};

const normalizeKeyLabelList = (raw = []) =>
  Array.isArray(raw)
    ? raw.map((s, i) => {
        if (typeof s === "string") {
          const k = String(s).toUpperCase();
          return { key: k, label: s };
        }
        const keyRaw = s?.key ?? s?.id ?? s?.code ?? String(i + 1);
        const key = String(keyRaw).toUpperCase();
        const label = s?.label ?? s?.name ?? s?.title ?? String(keyRaw);
        return { key, label };
      })
    : [];

/* ---------- Test Type helpers (new schema with legacy fallback) ---------- */
const humanize = (s = "") =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();

// label used for UI; suppress if it collides with run-mode wording
const getTestTypeLabel = (obj = {}) => {
  const lbl =
    obj.testTypeLabel ||
    obj.examModeLabel ||               // legacy template field
    (obj.testType ? humanize(obj.testType) : "") ||
    (obj.examMode ? humanize(obj.examMode) : ""); // legacy fallback

  const clean = (lbl || "").trim();
  return /^(live|mock|anytime)$/i.test(clean) ? "" : clean;
};

const testTypeBadgeNode = (obj) => {
  const lbl = getTestTypeLabel(obj);
  if (!lbl) return null;
  return (
    <Badge bg="light" text="dark" title={`Test Type: ${lbl}`}>
      {lbl}
    </Badge>
  );
};


/* ======================================================================== */

export default function ExamsPage() {
  const { schoolId, school, loading: schoolLoading } = useSchool();
  const nav = useNavigate();

  const [tplLoading, setTplLoading] = useState(true);
  const [examLoading, setExamLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [exams, setExams] = useState([]);
  const [recentScheduled, setRecentScheduled] = useState([]);
  const [search, setSearch] = useState("");

  // filters (templates)
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  // filters (recent)
  const [recentSearch, setRecentSearch] = useState("");
  const [recentPublishFilter, setRecentPublishFilter] = useState(""); // "", "published", "scheduled"
  const [recentClass, setRecentClass] = useState("");                  // classId or className
  const [recentSection, setRecentSection] = useState("");              // sectionKey or "" for whole class
  const [recentBranch, setRecentBranch] = useState("");                // branchKey or "" for any

  // toasts
  const [toastMsg, setToastMsg] = useState("");
  const showToast = (msg) => setToastMsg(msg);
  // how many times a student can resume a live exam after it starts
const [allowedResumes, setAllowedResumes] = useState(1);
  /* ========= SUBSCRIPTION GATE ========= */
  const subStatus = String(school?.subscription?.status || "").toLowerCase();
  const hasExamAccess = subStatus === "active" || subStatus === "trial";
  const renewalAt = school?.subscription?.renewalDate?.toDate
    ? dayjs(school.subscription.renewalDate.toDate())
    : null;

  /* ---------- Load recent templates (live) ---------- */
  useEffect(() => {
    if (!schoolId || !hasExamAccess) return;
    setTplLoading(true);
    const ref = collection(db, "schools", schoolId, "examTemplates");
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(48));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setTemplates(rows);
        setTplLoading(false);
      },
      () => setTplLoading(false)
    );
    return () => unsub();
  }, [schoolId, hasExamAccess]);

  /* ---------- Load upcoming exams (live) ---------- */
  useEffect(() => {
    if (!schoolId || !hasExamAccess) return;
    setExamLoading(true);
    const today = dayjs().format("YYYY-MM-DD");
    const ref = collection(db, "schools", schoolId, "exams");
    const qy = query(ref, where("examMode", "==", "live"), where("date", ">=", today), orderBy("date", "asc"), limit(20));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setExams(rows);
        setExamLoading(false);
      },
      () => setExamLoading(false)
    );
    return () => unsub();
  }, [schoolId, hasExamAccess]);

  /* ---------- Load recent scheduled list (live, newest first) ---------- */
  useEffect(() => {
    if (!schoolId || !hasExamAccess) return;
    const ref = collection(db, "schools", schoolId, "exams");
    const qy = query(ref, orderBy("createdAt", "desc"), limit(20));
    const unsub = onSnapshot(qy, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
      setRecentScheduled(rows);
    });
    return () => unsub();
  }, [schoolId, hasExamAccess]);

  /* ---------- OPEN MOCK/PRACTICE TESTS ---------- */
  const [mockExams, setMockExams] = useState([]);
  const [mockLoading, setMockLoading] = useState(true);

  useEffect(() => {
    if (!schoolId || !hasExamAccess) return;
    setMockLoading(true);
    const ref = collection(db, "schools", schoolId, "exams");
    const qy = query(
      ref,
      where("examMode", "==", "mock"),
      where("isPublished", "==", true),
      orderBy("updatedAt", "desc"),
      limit(20)
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        setMockExams(rows);
        setMockLoading(false);
      },
      () => setMockLoading(false)
    );
    return () => unsub();
  }, [schoolId, hasExamAccess]);

  /* ---------- AUTO-PUBLISH HEARTBEAT ---------- */
  const processedRef = useRef(new Set());
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!schoolId || exams.length === 0) return;
    exams.forEach((e) => {
      if (e.examMode === "mock") return; // mocks don't auto-publish
      if (!e?.autoPublishAtStart || e?.isPublished) return;
      const at = toJsDate(e.publishAt);
      if (at && dayjs().isAfter(at) && !processedRef.current.has(e.id)) {
        processedRef.current.add(e.id);
        updateDoc(doc(db, "schools", schoolId, "exams", e.id), {
          isPublished: true,
          status: "published",
          visibility: "published",
          updatedAt: serverTimestamp(),
        }).catch(() => {
          processedRef.current.delete(e.id);
        });
      }
    });
  }, [nowTick, exams, schoolId]);

  /* ===================== SCHEDULING / EDITING ===================== */
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedTpl, setSchedTpl] = useState(null);
  const [editingExam, setEditingExam] = useState(null);

  // date/time
  const [schedDate, setSchedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState("10:00");
  const [durationMins, setDurationMins] = useState(60);
  const [endTime, setEndTime] = useState("11:00");

  // publishing: 'now' | 'auto' | 'manual'
  const [publishMode, setPublishMode] = useState("auto");

  // exam run mode (unchanged)
  const [examMode, setExamMode] = useState("live"); // 'live' | 'mock'
  useEffect(() => {
    if (examMode === "mock" && publishMode === "auto") setPublishMode("manual");
  }, [examMode]); // eslint-disable-line

  // classes, sections, branches
  const [classLoading, setClassLoading] = useState(false);
  const [allClasses, setAllClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [classMap, setClassMap] = useState({});
  const [selectedSections, setSelectedSections] = useState({});
  const [selectedBranches, setSelectedBranches] = useState({});

  const recomputeEndTime = (st = startTime, dur = durationMins) => {
    const [h, m] = String(st || "00:00").split(":").map((x) => parseInt(x || 0, 10));
    const total = h * 60 + m + (parseInt(dur || 0, 10) || 0);
    const hh = Math.floor((total % (24 * 60)) / 60).toString().padStart(2, "0");
    const mm = (total % 60).toString().padStart(2, "0");
    setEndTime(`${hh}:${mm}`);
  };
  useEffect(() => { recomputeEndTime(startTime, durationMins); }, [startTime, durationMins]); // eslint-disable-line

  const fetchAllClasses = async (sid, tplOrExam) => {
    const snap = await getDocs(collection(db, "schools", sid, "classes"));
    let opts = [];
    let map = {};
    snap.forEach((d) => {
      const data = d.data() || {};
      const id = d.id;
      const name = data.name || data.className || id;
      const sections = normalizeKeyLabelList(data.sections);
      const branches = normalizeKeyLabelList(data.branches);
      map[id] = { id, name, sections, branches };
      opts.push({ value: id, label: name });
    });

    if (opts.length > 0) {
      opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      return { opts, map };
    }

    // fallback to template/exam info for labels
    const tpl = tplOrExam || {};
    const tplClassIds = Array.isArray(tpl?.classIds) ? tpl.classIds.filter(Boolean) : [];
    const tplSectionIds = Array.isArray(tpl?.sectionIds)
      ? tpl.sectionIds.map((k) => String(k).toUpperCase())
      : [];
    const tplBranchIds = Array.isArray(tpl?.branchIds)
      ? tpl.branchIds.map((k) => String(k).toUpperCase())
      : [];

    if (tplClassIds.length > 0) {
      opts = tplClassIds.map((cid) => ({ value: cid, label: cid }));
      map = tplClassIds.reduce((acc, cid) => {
        acc[cid] = {
          id: cid,
          name: cid,
          sections: tplSectionIds.length ? tplSectionIds.map((k) => ({ key: k, label: k })) : [],
          branches: tplBranchIds.length ? tplBranchIds.map((k) => ({ key: k, label: k })) : [],
        };
        return acc;
      }, {});
    }

    return { opts, map };
  };

  const buildSelectionsFromTemplate = (tpl, cmap) => {
    const templateClassIds = Array.isArray(tpl.classIds) ? tpl.classIds : [];
    const availableClassIds = Object.keys(cmap);
    const classIds = templateClassIds.filter((cid) => availableClassIds.includes(cid));

    const byClassSections = (tpl.sectionKeysByClass && typeof tpl.sectionKeysByClass === "object")
      ? tpl.sectionKeysByClass
      : null;
    const flatSec = Array.isArray(tpl.sectionIds)
      ? tpl.sectionIds.map((k) => String(k).toUpperCase())
      : null;

    const byClassBranches = (tpl.branchKeysByClass && typeof tpl.branchKeysByClass === "object")
      ? tpl.branchKeysByClass
      : null;
    const flatBr = Array.isArray(tpl.branchIds)
      ? tpl.branchIds.map((k) => String(k).toUpperCase())
      : null;

    const selSections = {};
    const selBranches = {};

    classIds.forEach((cid) => {
      const secs = cmap[cid]?.sections || [];
      const brs = cmap[cid]?.branches || [];
      const secKeysInClass = new Set(secs.map((s) => String(s.key).toUpperCase()));
      const brKeysInClass = new Set(brs.map((b) => String(b.key).toUpperCase()));

      // sections
      if (byClassSections && Array.isArray(byClassSections[cid]) && byClassSections[cid].length) {
        const hits = byClassSections[cid].map((k) => String(k).toUpperCase()).filter((k) => secKeysInClass.has(k));
        selSections[cid] = new Set(hits.length ? hits : (secs.length ? secs.map((s) => s.key) : [""]));
      } else if (flatSec && flatSec.length) {
        const hits = flatSec.filter((k) => secKeysInClass.has(k));
        selSections[cid] = new Set(hits.length ? hits : (secs.length ? secs.map((s) => s.key) : [""]));
      } else {
        selSections[cid] = new Set(secs.length ? secs.map((s) => s.key) : [""]);
      }

      // branches
      if (byClassBranches && Array.isArray(byClassBranches[cid]) && byClassBranches[cid].length) {
        const hits = byClassBranches[cid].map((k) => String(k).toUpperCase()).filter((k) => brKeysInClass.has(k));
        selBranches[cid] = new Set(hits.length ? hits : (brs.length ? brs.map((b) => b.key) : [""]));
      } else if (flatBr && flatBr.length) {
        const hits = flatBr.filter((k) => brKeysInClass.has(k));
        selBranches[cid] = new Set(hits.length ? hits : (brs.length ? brs.map((b) => b.key) : [""]));
      } else {
        selBranches[cid] = new Set(brs.length ? brs.map((b) => b.key) : [""]);
      }
    });

    return { classIds, selectedSections: selSections, selectedBranches: selBranches };
  };

  const buildSelectionsFromExam = (examDoc, cmap) => {
    const classIds = Array.isArray(examDoc.classIds) ? examDoc.classIds.filter((cid) => cmap[cid]) : [];

    const byClassSec = (examDoc.sectionKeysByClass && typeof examDoc.sectionKeysByClass === "object")
      ? examDoc.sectionKeysByClass
      : {};
    const byClassBr = (examDoc.branchKeysByClass && typeof examDoc.branchKeysByClass === "object")
      ? examDoc.branchKeysByClass
      : {};

    const selSec = {};
    const selBr = {};

    classIds.forEach((cid) => {
      const secs = cmap[cid]?.sections || [];
      const brs = cmap[cid]?.branches || [];
      const secKeysInClass = new Set(secs.map((s) => String(s.key).toUpperCase()));
      const brKeysInClass = new Set(brs.map((b) => String(b.key).toUpperCase()));

      // sections
      const rawS = Array.isArray(byClassSec[cid]) ? byClassSec[cid] : [];
      const hitsS = rawS.map((k) => String(k).toUpperCase()).filter((k) => secKeysInClass.has(k));
      selSec[cid] = new Set(hitsS.length ? hitsS : (secs.length ? secs.map((s) => s.key) : [""]));

      // branches
      const rawB = Array.isArray(byClassBr[cid]) ? byClassBr[cid] : [];
      const hitsB = rawB.map((k) => String(k).toUpperCase()).filter((k) => brKeysInClass.has(k));
      selBr[cid] = new Set(hitsB.length ? hitsB : (brs.length ? brs.map((b) => b.key) : [""]));
    });

    return { classIds, selectedSections: selSec, selectedBranches: selBr };
  };

  const openSchedule = async (tpl) => {
    setSchedTpl(tpl);
    setEditingExam(null);
    setSchedOpen(true);
    setPublishMode("auto");
    setExamMode("live");
    setSchedDate(dayjs().format("YYYY-MM-DD"));
    setStartTime("10:00");
    setDurationMins(60);
    setAllowedResumes(1);

    setClassLoading(true);
    try {
      const { opts, map } = await fetchAllClasses(schoolId, tpl);
      setAllClasses(opts);
      setClassMap(map);

      const { classIds, selectedSections: selS, selectedBranches: selB } = buildSelectionsFromTemplate(tpl, map);
      setSelectedClassIds(classIds);
      setSelectedSections(selS);
      setSelectedBranches(selB);
    } catch (e) {
      showToast(e?.message || "Failed to load classes");
    } finally {
      setClassLoading(false);
    }
  };

  const openEditExam = async (examDoc) => {
    setSchedTpl({
      id: examDoc.templateId,
      name: examDoc.name,
      examType: examDoc.examType,
      // include test type fields for safety
      testType: examDoc.testType,
      testTypeLabel: examDoc.testTypeLabel
    });
    setEditingExam(examDoc);
    setSchedOpen(true);
setAllowedResumes(
  Number.isFinite(Number(examDoc?.allowedResumes)) ? Number(examDoc.allowedResumes) : 1
);
    setSchedDate(examDoc.date || dayjs().format("YYYY-MM-DD"));
    setStartTime(examDoc.startTime || "10:00");
    setDurationMins(examDoc.durationMins || 60);
    setEndTime(examDoc.endTime || "11:00");

    const mode = examDoc.isPublished ? "now" : (examDoc.autoPublishAtStart ? "auto" : "manual");
    setPublishMode(mode);
    setExamMode(examDoc.examMode || "live");

    setClassLoading(true);
    try {
      const { opts, map } = await fetchAllClasses(schoolId, examDoc);
      setAllClasses(opts);
      setClassMap(map);

      const { classIds, selectedSections: selS, selectedBranches: selB } = buildSelectionsFromExam(examDoc, map);
      setSelectedClassIds(classIds);
      setSelectedSections(selS);
      setSelectedBranches(selB);
    } catch (e) {
      showToast(e?.message || "Failed to load classes");
    } finally {
      setClassLoading(false);
    }
  };

  const closeSchedule = () => {
    setSchedOpen(false);
    setSchedTpl(null);
    setEditingExam(null);
    setSelectedClassIds([]);
    setSelectedSections({});
    setSelectedBranches({});
  };

  // When classes list changes, seed default selections
  useEffect(() => {
    setSelectedSections((prev) => {
      const next = {};
      selectedClassIds.forEach((cid) => {
        const c = classMap[cid];
        if (!c) return;
        if (prev[cid]) next[cid] = prev[cid];
        else {
          const secs = c.sections || [];
          next[cid] = new Set(secs.length ? secs.map((s) => s.key) : [""]);
        }
      });
      return next;
    });
    setSelectedBranches((prev) => {
      const next = {};
      selectedClassIds.forEach((cid) => {
        const c = classMap[cid];
        if (!c) return;
        if (prev[cid]) next[cid] = prev[cid];
        else {
          const brs = c.branches || [];
          next[cid] = new Set(brs.length ? brs.map((b) => b.key) : [""]);
        }
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassIds, classMap]);

  const toggleClassAllSections = (classId, checked) => {
    const c = classMap[classId];
    setSelectedSections((prev) => {
      const copy = { ...prev };
      if (!c || (c.sections || []).length === 0) {
        copy[classId] = checked ? new Set([""]) : new Set();
      } else {
        copy[classId] = checked ? new Set(c.sections.map((s) => s.key)) : new Set();
      }
      return copy;
    });
  };

  const toggleClassAllBranches = (classId, checked) => {
    const c = classMap[classId];
    setSelectedBranches((prev) => {
      const copy = { ...prev };
      if (!c || (c.branches || []).length === 0) {
        copy[classId] = checked ? new Set([""]) : new Set();
      } else {
        copy[classId] = checked ? new Set(c.branches.map((b) => b.key)) : new Set();
      }
      return copy;
    });
  };

  const toggleSection = (classId, secKey, checked) => {
    const key = String(secKey).toUpperCase();
    setSelectedSections((prev) => {
      const copy = { ...prev };
      const curr = new Set(copy[classId] || []);
      if (checked) curr.add(key);
      else curr.delete(key);
      if (key !== "") curr.delete("");
      copy[classId] = curr;
      return copy;
    });
  };

  const toggleBranch = (classId, brKey, checked) => {
    const key = String(brKey).toUpperCase();
    setSelectedBranches((prev) => {
      const copy = { ...prev };
      const curr = new Set(copy[classId] || []);
      if (checked) curr.add(key);
      else curr.delete(key);
      if (key !== "") curr.delete("");
      copy[classId] = curr;
      return copy;
    });
  };

  const setTemplateStatus = async (tpl, status) => {
    if (!schoolId) return;
    try {
      await setDoc(
        doc(db, "schools", schoolId, "examTemplates", tpl.id),
        { status, updatedAt: serverTimestamp() },
        { merge: true }
      );
      showToast(`Marked as ${status}.`);
    } catch (e) {
      showToast(e?.message || "Failed to update status.");
    }
  };

  const statusBadge = (status) => {
    const s = String(status || "draft").toLowerCase();
    const map = { published: "success", scheduled: "info", ready: "success", draft: "secondary" };
    return <Badge bg={map[s] || "secondary"} className="text-uppercase">{s}</Badge>;
  };

  const typeBadge = (t) => {
    const s = String(t || "").toLowerCase();
    const map = { objective: "info", descriptive: "warning", mixed: "primary" };
    return <Badge bg={map[s] || "light"} text={map[s] ? undefined : "dark"} className="text-uppercase">{t || "—"}</Badge>;
  };

  const modeBadge = (m) => {
    const s = String(m || "live").toLowerCase();
    if (s === "mock") return <Badge bg="dark">Anytime</Badge>;
    return <Badge bg="primary">Live</Badge>;
  };

  const setExamPublish = async (exam, publish) => {
    if (!schoolId) return;
    try {
      const payload = {
        isPublished: !!publish,
        visibility: publish ? "published" : "scheduled",
        status: publish ? "published" : "scheduled",
        ...(publish ? { publishAt: dayjs().toISOString() } : {}),
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "schools", schoolId, "exams", exam.id), payload);
      showToast(publish ? "Exam published." : "Exam hidden.");
    } catch (e) {
      showToast(e?.message || "Failed to update.");
    }
  };

  // -------------------- Schedule or Update Exam --------------------
  const scheduleExam = async () => {
    if (!schoolId || (!schedTpl && !editingExam)) return;

    // Build targets combining Sections x Branches for each class
    const targets = [];
    selectedClassIds.forEach((classId) => {
      const c = classMap[classId];
      const secs = c?.sections || [];
      const brs = c?.branches || [];
const testTypeFromTpl = (schedTpl?.testType) || (schedTpl?.examMode);
const testTypeLabelFromTpl =
  (schedTpl?.testTypeLabel) ||
  (schedTpl?.examModeLabel) ||
  (testTypeFromTpl ? humanize(testTypeFromTpl) : "");
      const selSec = selectedSections[classId] || new Set();
      const selBr = selectedBranches[classId] || new Set();

      const selectedSecList = secs.length
        ? secs.filter((s) => selSec.has(String(s.key).toUpperCase()))
        : (selSec.has("") ? [{ key: "", label: "" }] : []);
      const selectedBrList = brs.length
        ? brs.filter((b) => selBr.has(String(b.key).toUpperCase()))
        : (selBr.has("") ? [{ key: "", label: "" }] : [{ key: "", label: "" }]);

      const secList = selectedSecList.length ? selectedSecList : [{ key: "", label: "" }];
      const brList = selectedBrList.length ? selectedBrList : [{ key: "", label: "" }];

      secList.forEach((s) => {
        brList.forEach((b) => {
          targets.push({
            classId,
            className: c?.name || classId,
            sectionKey: String(s.key || "").toUpperCase(),
            sectionLabel: s.label || (s.key ? String(s.key).toUpperCase() : ""),
            branchKey: String(b.key || "").toUpperCase(),
            branchLabel: b.label || (b.key ? String(b.key).toUpperCase() : ""),
          });
        });
      });
    });

    if (!targets.length) return showToast("Pick at least one class/section/branch.");

    // live requires date/time; mock can ignore
    if (examMode === "live") {
      if (!schedDate || !startTime || !durationMins) return showToast("Date, start time and duration are required.");
    }

    const classIds = Array.from(new Set(targets.map((t) => t.classId)));

    // Build sectionKeysByClass & branchKeysByClass
    const sectionKeysByClass = targets.reduce((acc, t) => {
      const up = (t.sectionKey || "").toString().toUpperCase();
      if (!acc[t.classId]) acc[t.classId] = [];
      if (up && !acc[t.classId].includes(up)) acc[t.classId].push(up);
      return acc;
    }, {});
    const branchKeysByClass = targets.reduce((acc, t) => {
      const up = (t.branchKey || "").toString().toUpperCase();
      if (!acc[t.classId]) acc[t.classId] = [];
      if (up && !acc[t.classId].includes(up)) acc[t.classId].push(up);
      return acc;
    }, {});

    const publishNow = publishMode === "now";
    const autoAtStart = publishMode === "auto" && examMode === "live";
    const publishAtDate = autoAtStart
      ? dayjs(`${schedDate} ${startTime}`, "YYYY-MM-DD HH:mm", true).toDate()
      : (publishNow ? new Date() : null);

    // Test type (copy from template or editing exam, with legacy fallback)
    const testType = (schedTpl?.testType) || (editingExam?.testType) || (schedTpl?.examMode) || (editingExam?.examMode);
    const testTypeLabel = getTestTypeLabel(schedTpl || editingExam || {});

    const common = {
      name: (editingExam?.name) || (schedTpl?.name) || "Exam",
      examType: (editingExam?.examType) || (schedTpl?.examType) || "objective",
      // run mode
      examMode, // 'live' | 'mock'
      // ✅ persist test type to the scheduled exam
      testType: testType || "daily_test",
      testTypeLabel,
      targets,
      classIds,
      sectionKeysByClass,
      branchKeysByClass,
      updatedAt: serverTimestamp(),
    };

    let base;
    if (examMode === "live") {
      base = {
        ...common,
        status: publishNow ? "published" : "scheduled",
        visibility: publishNow ? "published" : "scheduled",
        isPublished: publishNow,
        autoPublishAtStart: autoAtStart,
        publishAt: publishAtDate,
        date: schedDate,
        startTime,
        endTime,
        durationMins: Number(durationMins) || 0,
         allowedResumes: Number(allowedResumes) || 0,
      };
    } else {
      base = {
        ...common,
        status: publishNow ? "published" : "scheduled",
        visibility: publishNow ? "published" : "scheduled",
        isPublished: publishNow,
        autoPublishAtStart: false,
        publishAt: publishNow ? new Date() : null,
        date: null,
        startTime: null,
        endTime: null,
        durationMins: null,
         allowedResumes: Number(allowedResumes) || 0, 
      };
    }

    try {
      if (editingExam) {
        await updateDoc(doc(db, "schools", schoolId, "exams", editingExam.id), base);
        showToast("Exam updated.");
      } else {
        const payload = {
          ...base,
          templateId: schedTpl.id,
          createdAt: serverTimestamp(),
        };
        await addDoc(collection(db, "schools", schoolId, "exams"), payload);

        if ((schedTpl.status || "draft").toLowerCase() !== "ready") {
          await setDoc(
            doc(db, "schools", schoolId, "examTemplates", schedTpl.id),
            { status: "ready", updatedAt: serverTimestamp() },
            { merge: true }
          );
        }
        showToast(publishNow ? "Exam scheduled & published." : "Exam scheduled.");
      }
      closeSchedule();
    } catch (e) {
      showToast(e?.message || "Failed to save exam.");
    }
  };

  const deleteExam = async (examDoc) => {
    if (!schoolId) return;
    const ok = window.confirm(`Delete exam "${examDoc.name}"? This cannot be undone.`);
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "schools", schoolId, "exams", examDoc.id));
      showToast("Exam deleted.");
    } catch (e) {
      showToast(e?.message || "Failed to delete exam.");
    }
  };

  /* ---------- Helpers for RECENT filters ---------- */
  const recentClassOptions = useMemo(() => {
    const set = new Map(); // key -> label
    (recentScheduled || []).forEach((e) => {
      (e.targets || []).forEach((t) => {
        const key = t.classId || t.className || "";
        const lbl = t.className || t.classId || "";
        if (key) set.set(key, lbl);
      });
    });
    const arr = Array.from(set.entries()).map(([value, label]) => ({ value, label }));
    arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return arr;
  }, [recentScheduled]);

  const recentSectionOptions = useMemo(() => {
    const set = new Set();
    (recentScheduled || []).forEach((e) => {
      (e.targets || []).forEach((t) => {
        const classKey = t.classId || t.className || "";
        if (recentClass && classKey !== recentClass) return;
        const sKey = (t.sectionKey || "").toString().toUpperCase(); // "" means whole class
        const label = (t.sectionLabel || (sKey || "Whole class"));
        set.add(JSON.stringify({ key: sKey, label }));
      });
    });
    const arr = Array.from(set).map((s) => JSON.parse(s));
    arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return arr;
  }, [recentScheduled, recentClass]);

  const recentBranchOptions = useMemo(() => {
    const set = new Set();
    (recentScheduled || []).forEach((e) => {
      (e.targets || []).forEach((t) => {
        const classKey = t.classId || t.className || "";
        if (recentClass && classKey !== recentClass) return;
        const bKey = (t.branchKey || "").toString().toUpperCase(); // "" means any / not used
        const label = t.branchLabel || (bKey || "Any branch");
        set.add(JSON.stringify({ key: bKey, label }));
      });
    });
    const arr = Array.from(set).map((s) => JSON.parse(s));
    arr.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    return arr;
  }, [recentScheduled, recentClass]);

  const filteredRecent = useMemo(() => {
    const q = recentSearch.trim().toLowerCase();
    return (recentScheduled || []).filter((e) => {
      if (q) {
        const hay = [
          e.name, e.examType, e.status, e.date, e.startTime, e.endTime,
          e.examMode,
          e.testType, e.testTypeLabel, // include test type fields in search
          ...(e.classIds || []),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (recentPublishFilter) {
        const isPub = !!e.isPublished || String(e.status || "").toLowerCase() === "published";
        if (recentPublishFilter === "published" && !isPub) return false;
        if (recentPublishFilter === "scheduled" && isPub) return false;
      }
      if (recentClass || recentSection || recentBranch) {
        const hits = (e.targets || []).some((t) => {
          const cKey = t.classId || t.className || "";
          if (recentClass && cKey !== recentClass) return false;
          if (recentSection) {
            const sKey = (t.sectionKey || "").toString().toUpperCase();
            if (sKey !== recentSection.toString().toUpperCase()) return false;
          }
          if (recentBranch) {
            const bKey = (t.branchKey || "").toString().toUpperCase();
            if (bKey !== recentBranch.toString().toUpperCase()) return false;
          }
          return true;
        });
        if (!hits) return false;
      }
      return true;
    });
  }, [recentScheduled, recentSearch, recentPublishFilter, recentClass, recentSection, recentBranch]);

  /* ---------- Loading gate ---------- */
  if (schoolLoading) {
    return (
      <div className="d-flex align-items-center justify-content-center py-5">
        <Spinner />
      </div>
    );
  }

  /* ---------- SUBSCRIPTION GATE UI ---------- */
  if (!hasExamAccess) {
    return (
      <div className="container-fluid p-0">
        <div className="hero-gradient rounded-3 p-4 mb-3">
          <h4 className="m-0 fw-bold">Examinations</h4>
          <div className="opacity-75">Access to Exams is unavailable for this school.</div>
        </div>

        <Card className="border-0 shadow-sm">
          <Card.Body>
            <Alert variant="warning" className="mb-0">
              <div className="fw-semibold mb-1">
                {subStatus === "expired" ? "Subscription expired" : "Subscription inactive"}
              </div>
              <div className="mb-2">
                Your school’s subscription status is <strong>{subStatus || "unknown"}</strong>.
                {renewalAt && (
                  <> Renewal date: <strong>{renewalAt.format("MMM D,  YYYY")}</strong>.</>
                )}
              </div>
              <div className="text-muted">
                Please renew or start a trial to use Exam templates, scheduling and publishing.
              </div>
            </Alert>
          </Card.Body>
        </Card>
      </div>
    );
  }

  /* ---------- Full page ---------- */
  return (
    <div className="container-fluid p-0">
      {/* Toasts */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast show={!!toastMsg} onClose={() => setToastMsg("")} delay={2500} autohide bg="dark">
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Hero Header */}
      <div className="hero-gradient rounded-3 p-4 mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h4 className="m-0 fw-bold">Examinations</h4>
            <div className="opacity-75">Create templates, manage questions, schedule and publish exams.</div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button variant="outline-success" onClick={() => nav("/school/exams/templates/new")}>
              + Create Template
            </Button>
            <Button variant="outline-secondary" onClick={() => window.location.reload()}>
              Refresh
            </Button>
            <Button variant="outline-success" onClick={() => nav("/school/exams/question-bank/new")}>
              Create Question Bank
            </Button>
          </div>
        </div>
      </div>

      {/* Filters Row (Templates) */}
      <Row className="g-3 align-items-end mb-2">
        <Col md={5}>
          <Form.Label className="mb-1">Search templates</Form.Label>
          <InputGroup>
            <InputGroup.Text>Search</InputGroup.Text>
            <Form.Control
              placeholder="Search by name, type, or test type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => setSearch("")}>
                Clear
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col md={3}>
          <Form.Label className="mb-1">Status</Form.Label>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="ready">Ready</option>
            <option value="draft">Draft</option>
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label className="mb-1">Type</Form.Label>
          <Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All</option>
            <option value="objective">Objective</option>
            <option value="descriptive">Descriptive</option>
            <option value="mixed">Mixed</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="g-3">
        {/* Upcoming Exams (Live) */}
        <Col lg={5}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Upcoming Exams</span>
              <Badge bg="light" text="dark">{examLoading ? "…" : exams.length}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {examLoading ? (
                <div className="p-4 text-center"><Spinner /></div>
              ) : exams.length === 0 ? (
                <div className="p-4 text-muted">No upcoming exams.</div>
              ) : (
                <div className="upcoming-exams-scroll">
                  <ListGroup variant="flush">
                    {exams.map((e) => {
                      const published = !!e.isPublished || String(e.status || "").toLowerCase() === "published";
                      return (
                        <ListGroup.Item key={e.id} className="py-3">
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded text-center fw-bold"
                                style={{
                                  width: 64,
                                  minWidth: 64,
                                  height: 64,
                                  background: "#f5f7fb",
                                  display: "grid",
                                  placeItems: "center"
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: 18 }}>{dayjs(e.date).format("DD")}</div>
                                  <div className="text-muted" style={{ fontSize: 12 }}>
                                    {dayjs(e.date).format("MMM")}
                                  </div>
                                </div>
                              </div>
                              <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <div className="fw-semibold">{e.name || "-"}</div>
                                  {statusBadge(e.status)}
                                  {typeBadge(e.examType)}
                                  {testTypeBadgeNode(e)}
                                  {modeBadge(e.examMode)}
                                </div>
                                <div className="text-muted small mt-1">
                                  {(e.examMode || "live") === "mock"
                                    ? "Available anytime"
                                    : `${e.startTime}–${e.endTime} · ${e.durationMins || 0} mins`}
                                  {e.autoPublishAtStart && (e.examMode !== "mock") && (
                                    <span className="ms-2">
                                      <Badge bg="light" text="dark">Auto-publish</Badge>
                                    </span>
                                  )}
                                </div>
                                {!!(e.targets || []).length && (
                                  <div className="mt-2">
                                    {(e.targets || []).slice(0, 3).map((t, i) => (
                                      <Badge key={i} bg="light" text="dark" className="me-1">
                                        {t.className}
                                        {t.sectionLabel ? ` · ${t.sectionLabel}` : ""}
                                        {t.branchLabel ? ` · ${t.branchLabel}` : ""}
                                      </Badge>
                                    ))}
                                    {(e.targets || []).length > 3 && (
                                      <Badge bg="light" text="dark">+{(e.targets || []).length - 3}</Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="d-flex gap-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => openEditExam(e)}
                                title="Edit exam"
                              >
                                Edit
                              </Button>
                              {published ? (
                                <Button
                                  size="sm"
                                  variant="outline-warning"
                                  onClick={() => setExamPublish(e, false)}
                                  title="Hide from students"
                                >
                                  Unpublish
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="success"
                                  onClick={() => setExamPublish(e, true)}
                                  title="Make visible to students"
                                >
                                  Publish
                                </Button>
                              )}
                              <Button size="sm" variant="outline-danger" onClick={() => deleteExam(e)}>
                                Delete
                              </Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Open Practice (Mock) Tests */}
          <Card className="border-0 shadow-sm mt-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Open Practice (Mock) Tests</span>
              <Badge bg="light" text="dark">{mockLoading ? "…" : mockExams.length}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {mockLoading ? (
                <div className="p-4 text-center"><Spinner /></div>
              ) : mockExams.length === 0 ? (
                <div className="p-4 text-muted">No open mock tests.</div>
              ) : (
                <div className="upcoming-exams-scroll">
                  <ListGroup variant="flush">
                    {mockExams.map((e) => (
                      <ListGroup.Item key={e.id} className="py-3">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded text-center fw-bold"
                              style={{ width: 64, minWidth: 64, height: 64, background: "#f5f7fb", display: "grid", placeItems: "center" }}
                            >
                              <div>
                                <div style={{ fontSize: 14 }}>ANY</div>
                                <div className="text-muted" style={{ fontSize: 12 }}>TIME</div>
                              </div>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <div className="fw-semibold">{e.name || "-"}</div>
                                {statusBadge(e.status)}
                                {typeBadge(e.examType)}
                                {testTypeBadgeNode(e)}
                                {modeBadge(e.examMode)}
                              </div>
                              <div className="text-muted small mt-1">
                                Published {e.updatedAt?.toDate ? dayjs(e.updatedAt.toDate()).fromNow() : "—"}
                              </div>
                              {!!(e.targets || []).length && (
                                <div className="mt-2">
                                  {(e.targets || []).slice(0, 4).map((t, i) => (
                                    <Badge key={i} bg="light" text="dark" className="me-1">
                                      {t.className}
                                      {t.sectionLabel ? ` · ${t.sectionLabel}` : ""}
                                      {t.branchLabel ? ` · ${t.branchLabel}` : ""}
                                    </Badge>
                                  ))}
                                  {(e.targets || []).length > 4 && (
                                    <Badge bg="light" text="dark">+{(e.targets || []).length - 4}</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="outline-primary" onClick={() => openEditExam(e)}>Edit</Button>
                            <Button size="sm" variant="outline-warning" onClick={() => setExamPublish(e, false)}>Unpublish</Button>
                            <Button size="sm" variant="outline-danger" onClick={() => deleteExam(e)}>Delete</Button>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* local styles for scroll */}
          <style>{`
            .upcoming-exams-scroll {
              max-height: 420px;
              overflow-y: auto;
              padding-right: 2px;
            }
            .upcoming-exams-scroll::-webkit-scrollbar { width: 8px; }
            .upcoming-exams-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,.15); border-radius: 8px; }
            .upcoming-exams-scroll:hover::-webkit-scrollbar-thumb { background: rgba(0,0,0,.25); }
          `}</style>
        </Col>

        {/* Recent Templates */}
        <Col lg={7}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Recent Templates</span>
              <Badge bg="light" text="dark">{tplLoading ? "…" : templates.length}</Badge>
            </Card.Header>

            <Card.Body>
              {tplLoading ? (
                <div className="p-4 text-center"><Spinner /></div>
              ) : templates.length === 0 ? (
                <div className="p-4 text-muted">No templates found. Create one to get started.</div>
              ) : (
                <div className="recent-templates-scroll">
                  <Row className="g-3">
                    {templates
                      .filter((t) => {
                        const s = search.toLowerCase().trim();
                        if (statusFilter && String(t.status || "draft").toLowerCase() !== statusFilter) return false;
                        if (typeFilter && String(t.examType || "").toLowerCase() !== typeFilter) return false;
                        if (!s) return true;
                        return (
                          String(t.name || "").toLowerCase().includes(s) ||
                          String(t.examType || "").toLowerCase().includes(s) ||
                          String(t.testType || t.examMode || "").toLowerCase().includes(s) ||
                          String(t.testTypeLabel || t.examModeLabel || "").toLowerCase().includes(s)
                        );
                      })
                      .map((t) => {
                        const ready = String(t.status || "draft").toLowerCase() === "ready";
                        return (
                          <Col key={t.id} md={12}>
                            <Card className="border-0 shadow-sm">
                              <Card.Body>
                                {/* Top row */}
                                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                                  <div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                      <span className="fw-semibold" style={{ fontSize: 16 }}>{t.name}</span>
                                      {statusBadge(t.status)}
                                      {typeBadge(t.examType)}
                                      {testTypeBadgeNode(t)}
                                      {/* Exam run mode badge is not relevant on templates, so omitted */}
                                    </div>
                                    <div className="text-muted small mt-1">
                                      Updated {t.updatedAt?.toDate ? dayjs(t.updatedAt.toDate()).fromNow() : "—"}
                                    </div>
                                  </div>

                                  {/* Quick figure */}
                                  <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Total questions</Tooltip>}>
                                      <div className="text-center">
                                        <div className="fw-bold" style={{ fontSize: 18 }}>{t.totalQuestions || 0}</div>
                                        <div className="text-muted small">Questions</div>
                                      </div>
                                    </OverlayTrigger>
                                  </div>
                                </div>

                                {/* Classes / tags */}
                                <div className="mt-3 d-flex flex-wrap gap-2">
                                  {(t.classIds || []).slice(0, 6).map((c) => (
                                    <Badge key={c} bg="light" text="dark">{c}</Badge>
                                  ))}
                                  {(t.classIds || []).length > 6 && (
                                    <Badge bg="light" text="dark">+{(t.classIds || []).length - 6}</Badge>
                                  )}
                                  {(t.classIds || []).length === 0 && (
                                    <span className="text-muted small">No classes attached</span>
                                  )}
                                </div>

                                {/* Action bar */}
                                <div className="mt-3 d-flex flex-wrap gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() =>
                                      nav(`/school/exams/templates/${t.id}`, { state: { templateSeed: t } })
                                    }
                                  >
                                    Edit
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={() => nav(`/school/exams/templates/${t.id}/questions`)}
                                    title="Open/Add questions for this template"
                                  >
                                    Questions
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant={ready ? "success" : "outline-success"}
                                    onClick={() => setTemplateStatus(t, ready ? "draft" : "ready")}
                                    title={ready ? "Mark as Draft" : "Mark Ready"}
                                  >
                                    {ready ? "Draft" : "Mark Ready"}
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => openSchedule(t)}
                                    disabled={!ready || (t.totalQuestions || 0) === 0}
                                    title={ready ? "Schedule exam" : "Template must be Ready"}
                                  >
                                    Schedule
                                  </Button>

                                  <Dropdown as={ButtonGroup} align="end">
                                    <Button size="sm" variant="outline-danger">
                                      Delete
                                    </Button>
                                    <Dropdown.Toggle split size="sm" variant="outline-danger" />
                                    <Dropdown.Menu>
                                      <Dropdown.Item
                                        className="text-danger"
                                        onClick={async () => {
                                          if (!schoolId) return;
                                          const ok = window.confirm(`Delete template "${t.name}" only? Questions will be kept.`);
                                          if (!ok) return;
                                          await deleteDoc(doc(db, "schools", schoolId, "examTemplates", t.id));
                                          showToast("Template deleted.");
                                        }}
                                      >
                                        Delete Template only
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        className="text-danger"
                                        onClick={async () => {
                                          if (!schoolId) return;
                                          const ok = window.confirm(
                                            `Delete template "${t.name}" AND all its questions?\nThis cannot be undone.`
                                          );
                                          if (!ok) return;

                                          await deleteDoc(doc(db, "schools", schoolId, "examTemplates", t.id));

                                          const qsRef = collection(db, "schools", schoolId, "questions");
                                          const qy = query(qsRef, where("templateId", "==", t.id), limit(500));
                                          const snap = await getDocs(qy);

                                          if (!snap.empty) {
                                            const batch = writeBatch(db);
                                            snap.forEach((d) => batch.delete(d.ref));
                                            await batch.commit();
                                          }
                                          showToast("Template and related questions deleted.");
                                        }}
                                      >
                                        Delete Template + Questions
                                      </Dropdown.Item>
                                    </Dropdown.Menu>
                                  </Dropdown>

                                  <div className="flex-grow-1" />
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => nav("/school/exams/templates/question-bank")}
                                    title="Browse the central question bank"
                                  >
                                    Question Bank
                                  </Button>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* local styles for scroll */}
          <style>{`
            .recent-templates-scroll {
              max-height: 420px;
              overflow-y: auto;
              padding-right: 2px;
            }
            .recent-templates-scroll::-webkit-scrollbar { width: 8px; }
            .recent-templates-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,.15); border-radius: 8px; }
            .recent-templates-scroll:hover::-webkit-scrollbar-thumb { background: rgba(0,0,0,.25); }
          `}</style>
        </Col>
      </Row>

      {/* Recent Scheduled (with filters + class/section/branch display) */}
      <Row className="g-3 mt-1">
        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="fw-semibold">Recent Scheduled</Card.Header>
            <Card.Body>
              {/* Filters for recent */}
              <Row className="g-2 align-items-end mb-3">
                <Col md={4}>
                  <Form.Label className="mb-1">Search</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>Search</InputGroup.Text>
                    <Form.Control
                      placeholder="Name, type, date, test type, mode…"
                      value={recentSearch}
                      onChange={(e) => setRecentSearch(e.target.value)}
                    />
                    {recentSearch && (
                      <Button variant="outline-secondary" onClick={() => setRecentSearch("")}>
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                <Col md={2}>
                  <Form.Label className="mb-1">Publish status</Form.Label>
                  <Form.Select
                    value={recentPublishFilter}
                    onChange={(e) => setRecentPublishFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Label className="mb-1">Class</Form.Label>
                  <Form.Select
                    value={recentClass}
                    onChange={(e) => {
                      setRecentClass(e.target.value);
                      setRecentSection("");
                      setRecentBranch("");
                    }}
                  >
                    <option value="">All</option>
                    {recentClassOptions.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="mb-1">Section</Form.Label>
                  <Form.Select
                    value={recentSection}
                    onChange={(e) => setRecentSection(e.target.value)}
                    disabled={!recentSectionOptions.length}
                  >
                    <option value="">All</option>
                    {recentSectionOptions.map((s) => (
                      <option key={s.key || "WHOLE"} value={s.key}>
                        {s.label || "Whole class"}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="mb-1">Branch</Form.Label>
                  <Form.Select
                    value={recentBranch}
                    onChange={(e) => setRecentBranch(e.target.value)}
                    disabled={!recentBranchOptions.length}
                  >
                    <option value="">All</option>
                    {recentBranchOptions.map((b) => (
                      <option key={b.key || "ANY"} value={b.key}>
                        {b.label || "Any branch"}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              {/* List */}
              {filteredRecent.length === 0 ? (
                <div className="p-4 text-muted">No recent exams.</div>
              ) : (
                <div className="recent-scroll">
                  <ListGroup variant="flush">
                    {filteredRecent.map((e) => {
                      const published = !!e.isPublished || String(e.status || "").toLowerCase() === "published";
                      const created = e.createdAt?.toDate ? dayjs(e.createdAt.toDate()).fromNow() : "—";

                      const classSectionBranchBadges = (() => {
                        const grouped = new Map();
                        (e.targets || []).forEach((t) => {
                          const cname = t.className || t.classId || "Class";
                          const sLabel = t.sectionLabel || (t.sectionKey ? String(t.sectionKey).toUpperCase() : "Whole class");
                          const bLabel = t.branchLabel || (t.branchKey ? String(t.branchKey).toUpperCase() : "");
                          if (!grouped.has(cname)) grouped.set(cname, new Map());
                          const secMap = grouped.get(cname);
                          if (!secMap.has(sLabel)) secMap.set(sLabel, new Set());
                          if (bLabel) secMap.get(sLabel).add(bLabel);
                        });

                        const nodes = [];
                        grouped.forEach((secMap, cname) => {
                          nodes.push(
                            <div key={cname} className="me-3 mb-2 d-inline-flex align-items-start gap-2 flex-wrap">
                              <Badge bg="light" text="dark">{cname}</Badge>
                              {[...secMap.entries()].map(([sec, branches]) => (
                                <span key={`${cname}-${sec}`} className="d-inline-flex align-items-center gap-1 flex-wrap">
                                  <Badge bg="secondary">{sec}</Badge>
                                  {[...branches].map((b) => (
                                    <Badge key={`${cname}-${sec}-${b}`} bg="info">{b}</Badge>
                                  ))}
                                </span>
                              ))}
                            </div>
                          );
                        });
                        return nodes.length ? nodes : <span className="text-muted small">No class assigned</span>;
                      })();

                      return (
                        <ListGroup.Item key={e.id} className="py-3">
                          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                            <div className="me-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-semibold">{e.name || "-"}</span>
                                {statusBadge(e.status)}
                                {typeBadge(e.examType)}
                                {testTypeBadgeNode(e)}
                                {modeBadge(e.examMode)}
                              </div>
                              <div className="text-muted small mt-1">
                                {(e.examMode || "live") === "mock"
                                  ? "Anytime · "
                                  : (e.date ? `${e.date} · ${e.startTime}–${e.endTime} · ${e.durationMins || 0} mins · ` : "")}
                                created {created}
                              </div>
                              <div className="mt-2">{classSectionBranchBadges}</div>
                            </div>
                            <div className="d-flex gap-2">
                              <Button size="sm" variant="outline-primary" onClick={() => openEditExam(e)}>Edit</Button>
                              {published ? (
                                <Button size="sm" variant="outline-warning" onClick={() => setExamPublish(e, false)}>Unpublish</Button>
                              ) : (
                                <Button size="sm" variant="success" onClick={() => setExamPublish(e, true)}>Publish</Button>
                              )}
                              <Button size="sm" variant="outline-danger" onClick={() => deleteExam(e)}>Delete</Button>
                            </div>
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* local styles for scroll area */}
        <style>{`
          .recent-scroll {
            max-height: 420px;
            overflow-y: auto;
            padding-right: 2px;
          }
          .recent-scroll::-webkit-scrollbar { width: 8px; }
          .recent-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,.15); border-radius: 8px; }
          .recent-scroll:hover::-webkit-scrollbar-thumb { background: rgba(0,0,0,.25); }
        `}</style>
      </Row>

      {/* Schedule / Edit Modal */}
      <Modal show={schedOpen} onHide={closeSchedule} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingExam ? `Edit Exam · ${editingExam.name || ""}` : `Schedule Exam${schedTpl ? ` · ${schedTpl.name}` : ""}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!schedTpl && !editingExam ? (
            <div className="p-4 text-center text-muted">No template selected.</div>
          ) : (
            <>
              {/* Exam Mode */}
              <Row className="g-3 mb-2">
                <Col md={12}>
                  <Form.Label className="fw-semibold mb-1">Exam Mode</Form.Label>
                  <div className="d-flex gap-4 flex-wrap">
                    <Form.Check
                      type="radio"
                      id="mode-live"
                      name="exam-mode"
                      label="Live (fixed date & time)"
                      checked={examMode === "live"}
                      onChange={() => setExamMode("live")}
                    />
                    <Form.Check
                      type="radio"
                      id="mode-mock"
                      name="exam-mode"
                      label="Mock / Practice (anytime)"
                      checked={examMode === "mock"}
                      onChange={() => setExamMode("mock")}
                    />
                  </div>
                  <Form.Text muted>
                    Mock tests are available to students anytime (control visibility using Publish / Unpublish).
                  </Form.Text>
                </Col>
              </Row>

              {/* Date/Time */}
              <Row className="g-3" aria-disabled={examMode === "mock"}>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={schedDate}
                      onChange={(e) => setSchedDate(e.target.value)}
                      disabled={examMode === "mock"}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={examMode === "mock"}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Duration (mins)</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      value={durationMins}
                      onChange={(e) => setDurationMins(e.target.value)}
                      disabled={examMode === "mock"}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>End Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={endTime}
                      readOnly
                      disabled={examMode === "mock"}
                    />
                    
                    <Form.Text muted>Calculated from start time + duration</Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
  <Form.Group>
    <Form.Label>Max resumes (student)</Form.Label>
    <Form.Control
      type="number"
      min={0}
      value={allowedResumes}
      onChange={(e) => setAllowedResumes(Math.max(0, parseInt(e.target.value || "0", 10)))}
      disabled={examMode === "mock"}
    />
    <Form.Text muted>
      Applies to <b>live</b> exams. 0 = resume disabled. Example: 3 means the student can re-open the test 3 times if the tab/app is closed.
    </Form.Text>
  </Form.Group>
</Col>
              </Row>

              <hr />

              {/* Publish options */}
              <Row className="g-3">
                <Col md={12}>
                  <Form.Label className="fw-semibold mb-1">Visibility</Form.Label>
                  <div className="d-flex gap-3 flex-wrap">
                    <Form.Check
                      type="radio"
                      id="pub-now"
                      name="pub-mode"
                      label="Publish now (visible immediately)"
                      checked={publishMode === "now"}
                      onChange={() => setPublishMode("now")}
                    />
                    <Form.Check
                      type="radio"
                      id="pub-auto"
                      name="pub-mode"
                      label="Auto-publish at start time"
                      checked={publishMode === "auto"}
                      onChange={() => setPublishMode("auto")}
                      disabled={examMode === "mock"}
                    />
                    <Form.Check
                      type="radio"
                      id="pub-manual"
                      name="pub-mode"
                      label="Keep hidden (publish later)"
                      checked={publishMode === "manual"}
                      onChange={() => setPublishMode("manual")}
                    />
                  </div>
                </Col>
              </Row>

              <hr />

              {/* Class & section & branch assignment */}
              <div className="d-flex align-items-center justify-content-between">
                <div className="fw-semibold">Assign to Classes, Sections & Branches</div>
                <Badge bg="light" text="dark">{selectedClassIds.length} classes selected</Badge>
              </div>

              {classLoading ? (
                <div className="p-3 text-center"><Spinner /></div>
              ) : (
                <>
                  <div className="mt-2">
                    <MultiSelectDropdown
                      options={allClasses}
                      value={selectedClassIds}
                      onChange={setSelectedClassIds}
                      placeholder="Choose classes…"
                    />
                    <Form.Text muted>Pick one or more classes. Then choose sections and branches below (if any).</Form.Text>
                  </div>

                  {selectedClassIds.length === 0 ? (
                    <Alert variant="secondary" className="mt-3 mb-0">Select at least one class to proceed.</Alert>
                  ) : (
                    <div className="mt-3">
                      {selectedClassIds.map((cid) => {
                        const c = classMap[cid];
                        if (!c) {
                          return (
                            <div key={cid} className="mb-3">
                              <div className="fw-semibold">{cid}</div>
                              <div className="text-muted small">Loading…</div>
                            </div>
                          );
                        }
                        const secs = c.sections || [];
                        const brs = c.branches || [];
                        const selS = selectedSections[cid] || new Set();
                        const selB = selectedBranches[cid] || new Set();

                        const hasSections = secs.length > 0;
                        const hasBranches = brs.length > 0;

                        const totalS = hasSections ? secs.length : 1;
                        const totalB = hasBranches ? brs.length : 1;

                        const selCountS = hasSections ? selS.size : (selS.has("") ? 1 : 0);
                        const selCountB = hasBranches ? selB.size : (selB.has("") ? 1 : 0);

                        const allTickedS = hasSections ? (selS.size === secs.length && secs.length > 0) : selS.has("");
                        const someTickedS = hasSections ? (selS.size > 0 && selS.size < secs.length) : false;

                        const allTickedB = hasBranches ? (selB.size === brs.length && brs.length > 0) : selB.has("");
                        const someTickedB = hasBranches ? (selB.size > 0 && selB.size < brs.length) : false;

                        return (
                          <Card key={cid} className="mb-2">
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="fw-semibold">
                                  {c.name}
                                  <span className="text-muted small ms-2">
                                    {hasSections ? `${selCountS}/${totalS} sections` : (selS.has("") ? "Whole class" : "Not selected")}
                                    {" · "}
                                    {hasBranches ? `${selCountB}/${totalB} branches` : "Any branch"}
                                  </span>
                                </div>

                                <div className="d-flex gap-4">
                                  <Form.Check
                                    type="checkbox"
                                    id={`all-sec-${cid}`}
                                    label={hasSections ? "All sections" : "Whole class"}
                                    checked={allTickedS}
                                    aria-checked={someTickedS ? "mixed" : allTickedS}
                                    ref={(el) => { if (el) el.indeterminate = someTickedS; }}
                                    onChange={(e) => toggleClassAllSections(cid, e.target.checked)}
                                  />
                                  <Form.Check
                                    type="checkbox"
                                    id={`all-br-${cid}`}
                                    label={hasBranches ? "All branches" : "Any branch"}
                                    checked={allTickedB}
                                    aria-checked={someTickedB ? "mixed" : allTickedB}
                                    ref={(el) => { if (el) el.indeterminate = someTickedB; }}
                                    onChange={(e) => toggleClassAllBranches(cid, e.target.checked)}
                                  />
                                </div>
                              </div>

                              {/* Sections */}
                              {secs.length === 0 ? (
                                <div className="text-muted small mt-2">
                                  No sections configured — scheduling will target the whole class.
                                </div>
                              ) : (
                                <Row className="mt-2">
                                  {secs.map((s) => (
                                    <Col key={`${cid}-${s.key}`} xs={6} md={4} lg={3} className="mb-1">
                                      <Form.Check
                                        type="checkbox"
                                        id={`${cid}-${s.key}`}
                                        label={s.label || s.key}
                                        checked={selS.has(String(s.key).toUpperCase())}
                                        onChange={(e) => toggleSection(cid, s.key, e.target.checked)}
                                      />
                                    </Col>
                                  ))}
                                </Row>
                              )}

                              {/* Branches */}
                              {brs.length === 0 ? (
                                <div className="text-muted small mt-2">
                                  No branches configured — exam will apply to any/unspecified branch.
                                </div>
                              ) : (
                                <Row className="mt-2">
                                  {brs.map((b) => (
                                    <Col key={`${cid}-br-${b.key}`} xs={6} md={4} lg={3} className="mb-1">
                                      <Form.Check
                                        type="checkbox"
                                        id={`${cid}-br-${b.key}`}
                                        label={b.label || b.key}
                                        checked={selB.has(String(b.key).toUpperCase())}
                                        onChange={(e) => toggleBranch(cid, b.key, e.target.checked)}
                                      />
                                    </Col>
                                  ))}
                                </Row>
                              )}
                            </Card.Body>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSchedule}>Close</Button>
          <Button
            variant={publishMode === "now" ? "success" : "primary"}
            onClick={scheduleExam}
            disabled={classLoading || (!schedTpl && !editingExam)}
            title={publishMode === "now"
              ? (editingExam ? "Update & Publish" : "Schedule & Publish")
              : (editingExam ? "Update" : "Schedule")}
          >
            {editingExam
              ? (publishMode === "now" ? "Update & Publish" : "Update")
              : (publishMode === "now" ? "Schedule & Publish" : "Schedule")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
